import { DateTime, Duration } from "luxon";
import { useMemo } from "react";
import useActiveAnalysesStream, {
  type ActiveAnalysis,
} from "../../../../api/useActiveAnalysesStream";
import { useIndexedFailureMetadata } from "../../../../api/useFailureMetadata";
import { useFailureValues } from "../../../../api/useIndexedFailureValues";
import { useManualFailures } from "../../../../api/useManualFailures";
import { useIndexedManualFailureValues } from "../../../../api/useManualFailureValues";
import { useMetrologyOverrides } from "../../../../api/useMetrologyOverrides";
import { useProjectCategories } from "../../../../api/useProjectCategories";
import { useProject, type Project } from "../../../../api/useProjects";
import { useQuantities, useQuantityIndex } from "../../../../api/useQuantities";
import { useSetups, type Setup } from "../../../../api/useSetups";
import type { Transforms } from "diagnostics-domain/dashboard/type";
import { uniqBy } from "diagnostics-lib/array";
import useGroups from "../../../../api/useGroups";
import type { BlockDiagramModelT } from "../schema/block/diagram";
import type { BlockPlotModelT } from "../schema/block/plot";
import type { PeriodT } from "../schema/period";
import { Range, Duration as PeriodDuration } from "../schema/period";
import type { ResourceT } from "../schema/resource";
import { ActiveAnalysisColumns } from "../schema/resource/analysis";
import {
  FaultComputedValueColumns,
  FaultConstantColumns,
  FaultValueColumns,
  QuantityAnalysisSetupComputedColumns,
  QuantityAnalysisValueColumns,
  QuantitySetupColumns,
  type FaultColumnsT,
  type QuantityColumnsT,
} from "../schema/resource/columns";
import type { QuantityResourceT } from "../schema/resource/quantity";
import {
  mapFaultResourceToDataset,
  mapFaultResourceToValue,
  mapQuantityResourceToDataset,
  mapQuantityResourceToValue,
} from "../utils/dataset";
import { getAvailableResourceKind } from "../utils/getAvailableResourceKind";
import { applyTransforms } from "../utils/transform";
import type { useAvailableResources } from "./useAvailableResources";
import useDistinctShape from "@ui/lib/hooks/useDistinctShape";
import type { FaultResourceT } from "../schema/resource/fault";
import { isRight } from "fp-ts/lib/Either";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import { useMultipleExternalGaps } from "../../../../api/useExternalGaps";
import type { Event } from "../../../../api/useEvents";

export type QuantityDataset = ({
  [key in QuantityColumnsT]: boolean | number | string | null | undefined;
} & {
  rowId: string;
  identifier: string;
})[];

export type FaultDataset = ({
  [key in FaultColumnsT]: boolean | number | string | null | undefined;
} & {
  rowId: string;
  identifier: string;
})[];

export type Dataset = {
  quantities: QuantityDataset;
  faults: FaultDataset;
  periodRange: { from: number; to: number };
  pending: boolean;
};

const emptyFaultDataset: FaultDataset = [];
const emptyEventsByFault = new Map<string, Event[]>();

export const useQuantityDataset = (
  block: BlockPlotModelT | BlockDiagramModelT,
  projectId: string,
  datasetResources: ResourceT[],
) => {
  const project = useProject(projectId);
  if (!project) throw new Error("Project not found");

  const { activeAnalyses, isStreaming } = useActiveAnalysesStream(projectId);
  const equipments = useDistinctShape(useProjectCategories(projectId, "EQUIPMENT"));
  const groups = useGroups(projectId);
  const quantityIndex = useQuantityIndex(projectId);
  const metrologyOverrides = useMetrologyOverrides(projectId);
  const quantities = useQuantities(projectId);
  const setups = useSetups(projectId);
  const stableBlock = useDistinctShape(block);
  const periodRange = useActiveAnalysesStreamPeriodRange(stableBlock.data.period, project, groups);

  const blockDataTransforms = stableBlock.data.transforms;
  const periodAnalyses = useMemo(
    () =>
      isStreaming
        ? []
        : activeAnalyses.filter(
            analysis => analysis.date >= periodRange.from && analysis.date <= periodRange.to,
          ),
    [isStreaming, activeAnalyses, periodRange.from, periodRange.to],
  );
  const externalGapAnalysisIds = useMemo(
    () => periodAnalyses.map(({ id }) => id),
    [periodAnalyses],
  );
  const externalGaps = useMultipleExternalGaps(projectId, externalGapAnalysisIds);

  const mapQuantityValue = useMemo(
    () =>
      mapQuantityResourceToValue({
        quantities,
        equipments,
        metrology: quantityIndex,
        groups,
        setups,
        metrologyOverrides,
        externalGaps,
        timezone: project.timezone,
      }),
    [
      quantities,
      equipments,
      quantityIndex,
      groups,
      setups,
      metrologyOverrides,
      externalGaps,
      project.timezone,
    ],
  );

  // compute quantity dataset
  const quantityDataset = useMemo(() => {
    const quantityResources = datasetResources.filter(r => r.kind === "QUANTITY");
    if (isStreaming) return [];
    return computeQuantityDataset({
      analyses: periodAnalyses,
      resources: quantityResources,
      transforms: blockDataTransforms,
      mapValue: mapQuantityValue,
    });
  }, [isStreaming, periodAnalyses, blockDataTransforms, datasetResources, mapQuantityValue]);

  const dataset = useMemo<Dataset>(
    () => ({
      quantities: quantityDataset,
      faults: emptyFaultDataset,
      periodRange,
      pending: isStreaming,
    }),
    [quantityDataset, periodRange, isStreaming],
  );

  return {
    dataset,
    project,
    quantities,
    groups,
    setups,
    periodAnalyses,
    isStreaming,
    blockDataTransforms,
    datasetResources,
  };
};

export const useFaultDataset = (
  input: ReturnType<typeof useQuantityDataset>,
  eventsByFault = emptyEventsByFault,
): Dataset => {
  const {
    dataset,
    project,
    quantities,
    groups,
    setups,
    periodAnalyses,
    isStreaming,
    blockDataTransforms,
    datasetResources,
  } = input;
  const failureValues = useFailureValues(project.id);
  const indexedFailureMetadata = useIndexedFailureMetadata(project.id);
  const manualFailures = useManualFailures(project.id);
  // Manual impacts may be derived from manual events under the existing feature flag.
  const indexedManualFailureValues = useIndexedManualFailureValues(project.id);

  const mapFaultValue = useMemo(
    () =>
      mapFaultResourceToValue({
        quantities,
        project,
        failureValueStatus: indexedFailureMetadata.failureValueStatus,
        groups,
        setups,
        failureValues,
        manualFailureValues: indexedManualFailureValues,
        manualFailures,
        eventsByFault,
        timezone: project.timezone,
      }),
    [
      quantities,
      project,
      groups,
      setups,
      failureValues,
      indexedManualFailureValues,
      indexedFailureMetadata,
      manualFailures,
      eventsByFault,
    ],
  );

  const faultDataset = useMemo(() => {
    const faultDatasetResources = datasetResources.filter(r => r.kind === "FAULT");
    if (isStreaming || faultDatasetResources.length === 0) return [];
    return computeFaultDataset({
      analyses: periodAnalyses,
      setups,
      resources: faultDatasetResources,
      transforms: blockDataTransforms,
      mapValue: mapFaultValue,
    });
  }, [periodAnalyses, blockDataTransforms, datasetResources, isStreaming, mapFaultValue, setups]);

  return useMemo(() => ({ ...dataset, faults: faultDataset }), [dataset, faultDataset]);
};

const useActiveAnalysesStreamPeriodRange = (
  period: PeriodT | undefined,
  project: Project,
  groups: ReturnType<typeof useGroups>,
) =>
  useMemo(() => {
    const earliestActiveAnalysisDate =
      project?.analysesAggregate?.earliestActiveAnalysis?.date ?? new Date(0).toISOString();
    const latestActiveAnalysisDate =
      project?.analysesAggregate?.latestActiveAnalysis?.date ?? new Date().toISOString();

    if (period === "CYCLE")
      return {
        from: groups.at(-1)?.startDate ?? -Infinity,
        to: groups.at(-1)?.endDate ?? Infinity,
      };

    if (period === "CYCLE_2")
      return {
        from: groups.at(-2)?.startDate ?? -Infinity,
        to: groups.at(-1)?.endDate ?? Infinity,
      };

    if (PeriodDuration.is(period) && period !== "ALL") {
      return {
        from: DateTime.fromISO(latestActiveAnalysisDate).minus(Duration.fromISO(period)).toMillis(),
        to: DateTime.fromISO(latestActiveAnalysisDate).toMillis(),
      };
    }

    if (Range.is(period)) {
      return {
        from:
          typeof period.from === "string"
            ? DateTime.max(
                DateTime.fromISO(earliestActiveAnalysisDate),
                DateTime.fromISO(period.from),
              ).toMillis()
            : (period.from ?? DateTime.fromISO(earliestActiveAnalysisDate).toMillis()),
        to:
          typeof period.to === "string"
            ? DateTime.min(
                DateTime.fromISO(latestActiveAnalysisDate),
                DateTime.fromISO(period.to),
              ).toMillis()
            : (period.to ?? DateTime.fromISO(latestActiveAnalysisDate).toMillis()),
      };
    }

    return {
      from: DateTime.fromISO(earliestActiveAnalysisDate).toMillis(),
      to: DateTime.fromISO(latestActiveAnalysisDate).toMillis(),
    };
  }, [
    groups,
    period,
    project?.analysesAggregate?.earliestActiveAnalysis?.date,
    project?.analysesAggregate?.latestActiveAnalysis?.date,
  ]);

export const useDatasetResources = (
  availableResources: ReturnType<typeof useAvailableResources>,
  block: BlockPlotModelT | BlockDiagramModelT,
) => {
  const encodings = useMemo(
    () =>
      block.config.layers.flatMap(layer => {
        if ("encoding" in layer && block.kind === "PLOT") return Object.values(layer.encoding);
        if ("encodings" in layer && block.kind === "DIAGRAM") return layer.encodings;
        return [];
      }),
    [block],
  );
  const resources = useMemo(
    () =>
      uniqBy(encodings, item => `${item.identifier}-${item.column}-${item.quantity}`)
        .filter(Boolean)
        .flatMap(({ identifier, column, quantity }) => {
          const kind = getAvailableResourceKind(availableResources, identifier);

          if (kind === "QUANTITY" && column !== null) {
            return [
              { identifier, column, kind, quantity },
              { identifier, kind, column: "normalizedGap", quantity },
            ];
          } else if (kind === "FAULT" && column !== null) {
            return [{ identifier, column, quantity, kind }];
          } else if (kind === "SYSTEM") {
            const faults = availableResources.fault.identifiers.filter(
              i => "system" in i && i.system === identifier,
            );
            return [
              ...availableResources.performanceIndicators.flatMap(pi =>
                faults.map(fault => ({
                  kind: "FAULT" as const,
                  identifier: fault.name,
                  column: "quantityImpacts",
                  quantity: pi?.name,
                })),
              ),
              ...faults.map(fault => ({
                kind: "FAULT" as const,
                identifier: fault.name,
                column: "systemId",
                quantity: undefined,
              })),
              ...faults.map(fault => ({
                kind: "FAULT" as const,
                identifier: fault.name,
                column: "eventId",
                quantity: undefined,
              })),
              ...faults.map(fault => ({
                kind: "FAULT" as const,
                identifier: fault.name,
                column: "eventHasMultiFault",
                quantity: undefined,
              })),
              ...faults.map(fault => ({
                kind: "FAULT" as const,
                identifier: fault.name,
                column: "eventType",
                quantity: undefined,
              })),
              ...faults.map(fault => ({
                kind: "FAULT" as const,
                identifier: fault.name,
                column: "eventName",
                quantity: undefined,
              })),
              ...faults.map(fault => ({
                kind: "FAULT" as const,
                identifier: fault.name,
                column: "eventFaultName",
                quantity: undefined,
              })),
              ...faults.map(fault => ({
                kind: "FAULT" as const,
                identifier: fault.name,
                column: "eventPriority",
                quantity: undefined,
              })),
              ...faults.map(fault => ({
                kind: "FAULT" as const,
                identifier: fault.name,
                column: "eventStartsAt",
                quantity: undefined,
              })),
              ...faults.map(fault => ({
                kind: "FAULT" as const,
                identifier: fault.name,
                column: "eventEndsAt",
                quantity: undefined,
              })),
            ];
          } else if (kind === "AGGREGATE") {
            return [
              ...availableResources.performanceIndicators.map(pi => ({
                kind: "QUANTITY" as const,
                identifier: pi.name,
                column: "gap",
              })),
              ...availableResources.performanceIndicators.flatMap(pi =>
                availableResources.fault.identifiers.map(fault => ({
                  kind: "FAULT" as const,
                  identifier: fault.name,
                  column: "quantityImpacts",
                  // This is needed to match ResourceT exactly
                  quantity: pi.name as string | undefined,
                })),
              ),
            ];
          } else return null;
        })
        .filter((resource): resource is NonNullable<ResourceT> => resource !== null),
    [availableResources, encodings],
  );
  return useDistinctShape(resources);
};

const computeQuantityDataset = ({
  analyses,
  transforms,
  resources,
  mapValue,
}: {
  analyses: ActiveAnalysis[];
  resources: QuantityResourceT[];
  transforms: Transforms | undefined;
  mapValue: ReturnType<typeof mapQuantityResourceToValue>;
}): QuantityDataset => {
  console.debug(`[INFO] computing quantity dataset...`);
  const hasAnalysisKeys = resources.some(
    r =>
      ActiveAnalysisColumns.decode(r.column)._tag === "Right" ||
      QuantityAnalysisSetupComputedColumns.decode(r.column)._tag === "Right" ||
      QuantityAnalysisValueColumns.decode(r.column)._tag === "Right",
  );
  const hasSetupKeys = resources.some(r => QuantitySetupColumns.decode(r.column)._tag === "Right");
  const mapDataset = mapQuantityResourceToDataset(resources);

  let result: { [key: string]: boolean | number | string | null | undefined }[] =
    hasAnalysisKeys || hasSetupKeys
      ? analyses.flatMap(analysis => mapDataset(mapValue(analysis)))
      : mapDataset(mapValue());

  if (transforms && transforms.length > 0) {
    result = applyTransforms(result, transforms);
  }

  console.debug(`[INFO] quantity dataset computed`);
  return result as QuantityDataset;
};

const computeFaultDataset = ({
  analyses,
  setups,
  resources,
  transforms,
  mapValue,
}: {
  analyses: ActiveAnalysis[];
  setups: Setup[];
  resources: FaultResourceT[];
  transforms: Transforms | undefined;
  mapValue: ReturnType<typeof mapFaultResourceToValue>;
}): FaultDataset => {
  console.debug(`[INFO] computing fault dataset...`);
  const hasAnalysisKeys = resources.some(
    r =>
      isRight(ActiveAnalysisColumns.decode(r.column)) ||
      isRight(FaultComputedValueColumns.decode(r.column)) ||
      isRight(FaultValueColumns.decode(r.column)) ||
      (isRight(FaultConstantColumns.decode(r.column)) && r.quantity),
  );
  const mapDataset = mapFaultResourceToDataset(resources);

  let result: { [key: string]: boolean | number | string | null | undefined }[] = [];

  const out = [];
  if (hasAnalysisKeys) {
    for (const analysis of analyses) {
      const setup = getAnalysisSetup(setups, analysis);
      const entry = setup ? mapDataset(mapValue({ analysis, setup })) : [];
      if (!entry) continue;
      out.push(...entry);
    }
  } else {
    for (const setup of setups) {
      const entry = mapDataset(mapValue({ setup }));
      if (!entry) continue;
      out.push(...entry);
    }
  }
  result = out;

  if (transforms && transforms.length > 0) {
    result = applyTransforms(result, transforms);
  }

  console.debug(`[INFO] fault dataset computed`);
  return result as FaultDataset;
};
